// Pedidos de exame / remoção lançados no IW (exportação EXAME_REMO no FTP) dentro das telas
// de Exames e de Remoções: mostra o que o médico pediu no IW, o que já está no Quality e
// abre o formulário de sempre já preenchido ("Usar"). Também mostra se há aditivo no IW
// para o paciente (só a parte assistencial — os valores nem chegam ao navegador).
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';

let cache = null;
let pedido = null;
const CACHE_MS = 5 * 60 * 1000;

function carregar(forcar = false) {
  if (!forcar && cache && Date.now() - cache.em < CACHE_MS) return Promise.resolve(cache.dados);
  if (pedido) return pedido;
  pedido = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const resp = await fetch('/api/iw-pedidos', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(json.error || `Erro ${resp.status}`);
    cache = { em: Date.now(), dados: json };
    return json;
  })().finally(() => { pedido = null; });
  return pedido;
}

const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
const PEQ = new Set(['DA', 'DE', 'DO', 'DAS', 'DOS', 'E']);
const tokens = s => norm(s).split(' ').filter(w => w && !PEQ.has(w));
function mesmoPaciente(a, b) {
  const x = tokens(a), y = tokens(b);
  if (x.length < 2 || y.length < 2) return false;
  if (x[0] !== y[0] || x[x.length - 1] !== y[y.length - 1]) return false;
  const sy = new Set(y);
  return x.filter(w => sy.has(w)).length >= Math.min(3, x.length, y.length);
}
const dataBR = iso => (iso ? iso.split('-').reverse().join('/') : '—');
const dataDe = v => {
  if (!v) return null;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return isNaN(d) ? null : d;
};

// "HB - UNIMED NI" → opção da lista da tela ("Unimed Nova Iguaçu"); vazio se não houver uma clara
const APELIDOS = { NI: 'NOVA IGUACU', LF: 'LESTE', 'V REDOND': 'VOLTA REDONDA', 'C FRIO': 'CABO FRIO', 'M VALENCA': 'VALENCA', GNDI: 'NOTREDAME' };
export function operadoraDaLista(nomeIW, lista = []) {
  let base = norm(nomeIW).replace(/^(HB|HS|VC)\s+/, '').replace(/\bSA$/, '').trim();
  for (const [a, b] of Object.entries(APELIDOS)) base = base.replace(new RegExp(`\\b${a}\\b`), b);
  const opcoes = lista.map(o => ({ o, n: norm(o) }));
  const exata = opcoes.find(x => x.n === base);
  if (exata) return exata.o;
  const bt = base.split(' ');
  let melhor = '', nota = 0;
  for (const { o, n } of opcoes) {
    const ot = n.split(' ');
    const casa = w => bt.includes(w) || bt.some(b => b.length >= 4 && w.startsWith(b.slice(0, 4)));
    if (!ot.some(w => w === bt[0] || (bt[0].length >= 4 && w.startsWith(bt[0].slice(0, 4))))) continue; // a 1ª palavra (UNIMED, PREVENT…) tem que bater
    const comum = ot.filter(casa).length;
    const s = comum / Math.max(ot.length, bt.length);
    if (s > nota) { nota = s; melhor = o; }
  }
  return nota >= 0.5 ? melhor : '';
}

// Aditivo do IW que corresponde ao pedido (mesmo atendimento e tipo de item)
function aditivoDoPedido(p, aditivos) {
  const tipo = p.tipo === 'exame' ? /exame/i : /unid\.? movel|remo/i;
  return aditivos
    .filter(a => a.numIW === p.numIW && (tipo.test(a.tipo) || tipo.test(a.item)))
    .sort((a, b) => (b.criadoEm || '').localeCompare(a.criadoEm || ''))[0] || null;
}

/**
 * Pedidos do IW de um tipo, marcando os que já estão no Quality, e o preenchimento do formulário.
 * Usado para mostrar os pedidos do IW DENTRO da lista da tela (etiqueta "Pedido no IW").
 * @returns {{ pedidos: object[], faltam: object[], prefill: (p) => object, carregado: boolean }}
 */
export function usePedidosIW({ tipo, existentes = [], operadoras = [] }) {
  const [dados, setDados] = useState(cache?.dados || null);
  useEffect(() => { carregar().then(setDados).catch(() => setDados({ pedidos: [], aditivos: [], erro: true })); }, []);
  const pedidos = useMemo(() => {
    if (!dados?.pedidos) return [];
    return dados.pedidos.filter(p => p.tipo === tipo).map(p => {
      const ref = new Date(`${p.data}T12:00:00`);
      const noQuality = existentes.find(r => r.iw_evolucao === p.evolucao) || existentes.find(r => {
        if (!mesmoPaciente(r.paciente, p.paciente)) return false;
        const d = dataDe(r.criadoEm) || dataDe(r.data) || dataDe(r.data_agendamento);
        return !d || (d - ref > -7 * 864e5 && d - ref < 60 * 864e5);
      });
      return { ...p, noQuality: !!noQuality, aditivo: aditivoDoPedido(p, dados.aditivos || []), operadoraLista: operadoraDaLista(p.operadora, operadoras) };
    }).sort((a, b) => (b.data || '').localeCompare(a.data || ''));
  }, [dados, tipo, existentes, operadoras]);
  const prefill = p => {
    const texto = `Pedido no IW em ${dataBR(p.data)} por ${p.profissional}${p.especialidade ? ` (${p.especialidade})` : ''}.
Justificativa: ${p.justificativa}`;
    return tipo === 'exame'
      ? { paciente: p.paciente, exame: p.pedido.slice(0, 200), operadora: p.operadoraLista, observacoes: texto, aditivo_iw: p.aditivo ? 'Sim' : '', iw_evolucao: p.evolucao, num_iw: p.numIW }
      : { paciente: p.paciente, plano: p.operadoraLista, motivo: p.pedido.slice(0, 160), observacao: texto, origem_solicitacao: 'Solicitação Médica', num_iw: p.numIW, iw_evolucao: p.evolucao };
  };
  return { pedidos, faltam: pedidos.filter(p => !p.noQuality), prefill, carregado: !!dados, atualizadoEm: dados?.atualizadoEm };
}

/**
 * Botão "🗂 Pedidos no IW" + janela.
 * @param tipo 'exame' | 'remocao'
 * @param existentes registros do Quality da mesma tela (para marcar o que já foi lançado)
 * @param operadoras opções de operadora/plano da tela
 * @param onUsar(prefill) abre o formulário de novo registro com os campos preenchidos
 */
export default function PedidosIW({ tipo, existentes = [], operadoras = [], onUsar }) {
  const [dados, setDados] = useState(cache?.dados || null);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(false);
  const [verTodos, setVerTodos] = useState(false);

  useEffect(() => { carregar().then(setDados).catch(e => setErro(e.message)); }, []);

  const lista = useMemo(() => {
    if (!dados) return [];
    return dados.pedidos.filter(p => p.tipo === tipo).map(p => {
      const ref = new Date(`${p.data}T12:00:00`);
      const noQuality = existentes.find(r => r.iw_evolucao === p.evolucao) || existentes.find(r => {
        if (!mesmoPaciente(r.paciente, p.paciente)) return false;
        const d = dataDe(r.criadoEm) || dataDe(r.data) || dataDe(r.data_agendamento);
        return !d || (d - ref > -7 * 864e5 && d - ref < 60 * 864e5);
      });
      return { ...p, noQuality: !!noQuality, aditivo: aditivoDoPedido(p, dados.aditivos || []) };
    }).sort((a, b) => (b.data || '').localeCompare(a.data || ''));
  }, [dados, tipo, existentes]);
  const faltam = lista.filter(p => !p.noQuality);
  const mostrar = verTodos ? lista : faltam;

  const usar = p => {
    const texto = `Pedido no IW em ${dataBR(p.data)} por ${p.profissional}${p.especialidade ? ` (${p.especialidade})` : ''}.\nJustificativa: ${p.justificativa}`;
    const op = operadoraDaLista(p.operadora, operadoras);
    const prefill = tipo === 'exame'
      ? { paciente: p.paciente, exame: p.pedido.slice(0, 200), operadora: op, observacoes: texto, aditivo_iw: p.aditivo ? 'Sim' : '', iw_evolucao: p.evolucao, num_iw: p.numIW }
      : { paciente: p.paciente, plano: op, motivo: p.pedido.slice(0, 160), observacao: texto, origem_solicitacao: 'Solicitação Médica', num_iw: p.numIW, iw_evolucao: p.evolucao };
    setAberto(false);
    onUsar(prefill);
  };

  if (erro) return null; // sem permissão ou IW fora do ar: a tela segue como sempre
  if (!dados) return null;
  const titulo = tipo === 'exame' ? 'Pedidos de exame no IW' : 'Pedidos de remoção no IW';
  const fmtHora = v => (v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', background: '#f8fafc' };
  const td = { padding: '8px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9' };

  return (
    <>
      <button type="button" onClick={() => setAberto(true)} title={`${titulo} (últimos 90 dias)`}
        style={{ border: '1px solid #a7f3d0', background: '#ecfdf5', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        🗂 Pedidos no IW
        {faltam.length > 0 && <span style={{ background: '#059669', color: '#fff', borderRadius: 999, padding: '0 7px', fontSize: '0.72rem' }}>{faltam.length}</span>}
      </button>
      {aberto && (
        <div role="dialog" aria-labelledby="titulo-pedidos-iw" onClick={() => setAberto(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1000, maxHeight: '88vh', overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div>
                <div id="titulo-pedidos-iw" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}>{titulo}</div>
                <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                  Exportação do IW de {fmtHora(dados.atualizadoEm)} · últimos 90 dias · {lista.length} pedido{lista.length === 1 ? '' : 's'}, {faltam.length} ainda não lançado{faltam.length === 1 ? '' : 's'} aqui
                </div>
              </div>
              <button onClick={() => setAberto(false)} aria-label="Fechar" style={{ border: 0, background: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.8rem', color: '#334155', margin: '12px 0' }}>
              <input type="checkbox" checked={verTodos} onChange={e => setVerTodos(e.target.checked)} /> Mostrar também os que já estão no Quality
            </label>
            {mostrar.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: 24, fontSize: '0.9rem' }}>Todos os pedidos do IW já estão lançados aqui.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: 820 }}>
                  <thead><tr>{['Data', 'Paciente', 'Operadora (IW)', tipo === 'exame' ? 'Exame pedido' : 'Remoção pedida', 'Pedido por', 'Aditivo no IW', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {mostrar.map(p => (
                      <tr key={p.evolucao}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataBR(p.data)}{p.pendente && <div style={{ fontSize: '0.7rem', color: '#b45309', fontWeight: 700 }}>pendência no IW</div>}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{p.paciente}<div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>IW {p.numIW}</div></td>
                        <td style={td}>{p.operadora}</td>
                        <td style={{ ...td, maxWidth: 300 }}>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{p.pedido || '—'}</div>
                          {p.justificativa && <details style={{ marginTop: 4 }}><summary style={{ cursor: 'pointer', fontSize: '0.74rem', color: '#2563eb' }}>Justificativa</summary><div style={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: '#334155' }}>{p.justificativa}</div></details>}
                        </td>
                        <td style={td}>{p.profissional}{p.especialidade && <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.especialidade}</div>}</td>
                        <td style={{ ...td, fontSize: '0.76rem' }}>
                          {p.aditivo
                            ? <span title={`Autorização interna: código ${p.aditivo.autorizacaoInterna} · operadora: código ${p.aditivo.autorizacaoOperadora}`} style={{ color: '#065f46', fontWeight: 700 }}>Sim · {dataBR(p.aditivo.criadoEm)}</span>
                            : <span style={{ color: '#94a3b8' }}>não encontrado</span>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {p.noQuality
                            ? <span style={{ fontSize: '0.74rem', color: '#166534', fontWeight: 700 }}>✓ já no Quality</span>
                            : <button type="button" onClick={() => usar(p)} style={{ border: 0, background: '#4f46e5', color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>Usar</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 12 }}>
              "Usar" abre o formulário de sempre já preenchido com o pedido do IW; confira e salve. "Já no Quality" = mesmo paciente lançado aqui perto da data do pedido.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
